import { useEffect, useState } from 'react';
import { Stack, Switch, Typography } from '@mui/material';
import dayjs from 'dayjs';
import Article from '../../components/Article';
import { useSnackbar } from '../../contexts/SnackBarContext';
import { useTenant } from '../../contexts/TenantContext';
import { tint } from '../../utils/textStyles';
import useMarketingConsent from './useMarketingConsent';

/**
 * The guest's own marketing switch. Switching off takes one tap and asks nothing: withdrawing has
 * to be as easy as consenting (GDPR 7(3)). The date shown is the day of the latest consent, the
 * one the tenant can prove.
 */
const MarketingConsentCard = ({ user }) => {
  const { tenant, displayConfig, homePage } = useTenant();
  const businessName = homePage?.businessName || tenant?.name;
  const textColor = displayConfig?.color;
  const accent = displayConfig?.primaryColor;
  const showSnackbar = useSnackbar();
  const { setConsent, loading } = useMarketingConsent();
  const [consentDate, setConsentDate] = useState(user?.marketingConsentDate ?? null);

  useEffect(() => {
    setConsentDate(user?.marketingConsentDate ?? null);
  }, [user?.marketingConsentDate]);

  const handleChange = async (event) => {
    const consented = event.target.checked;
    if (!(await setConsent(consented))) return;

    setConsentDate(consented ? dayjs().format('YYYY-MM-DD') : null);
    showSnackbar(consented ? 'Feliratkoztál a marketing levelekre' : 'Leiratkoztál a marketing levelekről', 'success');
  };

  return (
    <Article alignItems="stretch" xsHorizontalPadding={3} width="100%" sx={{ py: { xs: 2.5, sm: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack minWidth={0}>
          <Typography fontWeight={700} sx={{ color: textColor }}>
            Marketing levelek
          </Typography>
          <Typography fontSize={12} sx={{ color: tint(textColor, 0.7) }}>
            {consentDate
              ? `A(z) ${businessName} e-mailben tájékoztat az akciókról és újdonságokról. Feliratkozva: ${dayjs(consentDate).format('YYYY. MM. DD.')}`
              : `Kapcsold be, ha szeretnél e-mailt kapni a(z) ${businessName} akcióiról és újdonságairól.`}
          </Typography>
        </Stack>
        <Switch
          checked={Boolean(consentDate)}
          onChange={handleChange}
          disabled={loading}
          inputProps={{ 'aria-label': 'Marketing levelek' }}
          data-testid="marketing-consent-switch"
          sx={{
            flexShrink: 0,
            '& .MuiSwitch-switchBase.Mui-checked': { color: accent },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accent },
          }}
        />
      </Stack>
    </Article>
  );
};

export default MarketingConsentCard;
